import React, { useEffect, useMemo, useRef, useState } from "react";

import { Label } from "@fronterahealth/frontera-ui-components";

import { formatTimeDisplay } from "@pages/VideoSessionsV2/components/EmojiComments/useGetTickMarks";
import { EmojiMarkers } from "@pages/VideoSessionsV2/components/MediaTimeline/components/EmojiMarkers";
import { MomentDataRows } from "@pages/VideoSessionsV2/components/MediaTimeline/components/MomentDataRows";
import { MomentTitleRows } from "@pages/VideoSessionsV2/components/MediaTimeline/components/MomentTitleRows";
import {
  getBaseVisibleSeconds,
  getMajorInterval,
  getTickInterval,
} from "@pages/VideoSessionsV2/components/MediaTimeline/helper";
import { EmojiMarker } from "@pages/VideoSessionsV2/types";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

export type MediaTimelineEvent = {
  id: string;
  start: number; // in seconds
  end: number; // in seconds
  color: string; // Tailwind color class
  greyedOut?: boolean;
  aggregated?: boolean;
  goalId?: string;
  targetId?: string;
};

type MediaTimelineSubCategory = {
  id: string;
  title: string;
  events: MediaTimelineEvent[];
};

export type MediaTimelineCategory = {
  id: string;
  title: string;
  events: MediaTimelineEvent[];
  children: MediaTimelineSubCategory[];
  isPinned?: boolean;
};

type MediaTimelineChartProps = {
  data: MediaTimelineCategory[];
  emojiMarkers: EmojiMarker[];
  duration: number; // total video duration in seconds
  currentTime: number;
  onMomentClicked: (startTime: number, endTime: number) => void;
  onMarkerClick: (time: number, commentId: string) => void;
  zoomPercentValue: number;
  expandAll?: boolean;
  expandedRowIds: string[];
  toggleExpandRow: (goalId: string) => void;
};

export const MediaTimeline: React.FC<MediaTimelineChartProps> = ({
  data,
  emojiMarkers,
  duration,
  currentTime,
  onMomentClicked,
  onMarkerClick,
  zoomPercentValue,
  expandedRowIds,
  toggleExpandRow,
}) => {
  const [containerWidth, setContainerWidth] = useState(1200);
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineContentRef = useRef<HTMLDivElement>(null);

  const [unpinnedSectionCollapsed, setUnpinnedSectionCollapsed] = useState(false);

  const { activeMomentGoalId, activeMomentId } = useVideoSessionData();

  const getXPosition = (time: number): number => {
    return isFullyZoomedOut
      ? (time / duration) * containerWidth // spread evenly
      : time * pixelsPerSecond; // original behavior
  };
  const getWidth = (start: number, end: number) => {
    const rawWidth = end - start;
    return isFullyZoomedOut ? (rawWidth / duration) * containerWidth : rawWidth * pixelsPerSecond;
  };

  const zoomFactor = zoomPercentValue / 100; // 100% → 1, 150% → 1.5, etc.
  const baseVisibleSeconds = getBaseVisibleSeconds(duration);

  const visibleSeconds = baseVisibleSeconds / zoomFactor;

  const pixelsPerSecond = containerWidth / visibleSeconds;

  const timelineWidth = Math.max(duration * pixelsPerSecond, containerWidth);
  const isFullyZoomedOut = timelineWidth <= containerWidth;

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Scroll to active moment when goal is expanded
  useEffect(() => {
    if (activeMomentGoalId && activeMomentId && expandedRowIds.includes(activeMomentGoalId)) {
      const element = timelineContentRef.current?.querySelector(
        `[data-timeline-moment-id="${activeMomentGoalId}-${activeMomentId}"]`,
      );

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "end",
        });
      }
    }
  }, [activeMomentGoalId, activeMomentId, expandedRowIds]);

  const tickInterval = getTickInterval(pixelsPerSecond);
  const majorInterval = getMajorInterval(pixelsPerSecond, duration);

  // 3. Generate ticks for labels

  const ticks = useMemo(() => {
    const t: number[] = [];

    for (let s = 0; s <= duration; s += tickInterval) {
      t.push(s);
    }
    return t;
  }, [duration, tickInterval]);

  const Ticks = () => {
    return (
      <div className="z-10 h-10 border-b drop-shadow-md shadow-text-secondary bg-white">
        {ticks.map((s, index) => {
          return (
            <div key={s} className="absolute top-[18px]" style={{ left: getXPosition(s) }}>
              <Label
                size="extra-small"
                className="whitespace-nowrap absolute top-1 opacity-50"
                style={{
                  transform: index === 0 ? "none" : "translateX(-10%)",
                }}
              >
                {formatTimeDisplay(s)}
              </Label>
            </div>
          );
        })}
      </div>
    );
  };

  const VerticalLines = () => (
    <div className="absolute inset-0 pointer-events-none">
      {ticks.map((s) => {
        const isMajor = s % majorInterval === 0;
        return (
          isMajor && (
            <div
              key={s}
              className="absolute top-0 bottom-0 z-19 w-px bg-limestone-200 opacity-50"
              style={{ left: getXPosition(s) }}
            />
          )
        );
      })}
    </div>
  );

  const TIMELINE_MIN_WIDTH_PX = 1200; // minimum width of timeline area
  const TITLE_COLUMN_WIDTH_PX = 192; // w-48 in px

  return (
    <div className="w-full h-full flex flex-col">
      {/* Main container with proper sticky layout */}
      <div className="flex-1 overflow-auto" ref={timelineContentRef}>
        {/* Flex layout: sticky column + scrollable content. */}
        <div
          className="flex w-full"
          style={{ minWidth: Math.max(timelineWidth + TITLE_COLUMN_WIDTH_PX, TIMELINE_MIN_WIDTH_PX) }}
        >
          {/* Sticky left column */}
          <div className="w-48 sticky left-0 z-30 bg-white border-r shrink-0">
            {/* Left column header */}
            <div className="sticky top-0 z-40 bg-white">
              <div className="h-10 border-b bg-white drop-shadow-md shadow-text-secondary" />
              <div className="h-10 border-b bg-white" />
            </div>

            {/* Left column content */}
            <MomentTitleRows
              data={data}
              expandedRowIds={expandedRowIds}
              onToggleExpand={toggleExpandRow}
              unpinnedSectionCollapsed={unpinnedSectionCollapsed}
              onToggleUnpinnedSection={() => setUnpinnedSectionCollapsed(!unpinnedSectionCollapsed)}
            />
          </div>

          {/* Main timeline content area */}
          <div className="flex-1 min-w-0">
            <div className="relative" style={{ width: timelineWidth }}>
              {/* Sticky timeline header */}
              <div className="sticky top-0 z-20 bg-white">
                <Ticks />
                <EmojiMarkers emojiMarkers={emojiMarkers} onMarkerClick={onMarkerClick} getXPosition={getXPosition} />
              </div>

              {/* Timeline data rows */}
              <div className="relative">
                <MomentDataRows
                  data={data}
                  expandedRowIds={expandedRowIds}
                  onToggleExpand={toggleExpandRow}
                  unpinnedSectionCollapsed={unpinnedSectionCollapsed}
                  setUnpinnedSectionCollapsed={setUnpinnedSectionCollapsed}
                  getWidth={getWidth}
                  getXPosition={getXPosition}
                  onMomentClicked={onMomentClicked}
                />

                {/* Vertical grid lines */}
                <VerticalLines />
              </div>

              {/* Playhead - positioned absolutely within timeline */}
              <div
                className="absolute top-0 bottom-0 z-25 pointer-events-none w-px"
                style={{ left: getXPosition(currentTime) }}
              >
                <div className="sticky top-0 translate-x-[-50%] w-2 h-2 rounded-full bg-black" />
                <div className="absolute top-0 bottom-0 w-px bg-black left-0" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
